import * as df from 'date-fns';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';

import BooksDateCalendarHeader from '../../../../components/books/common/calendar/BooksDateCalendarHeader';
import BooksDateCalendarMonth, {
  BooksDateCalendarDay,
} from '../../../../components/books/common/calendar/BooksDateCalendarMonth';
import BooksSelectTimeAvailability from '../../../../components/books/common/inputs/BooksSelectTimeAvailability';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';

import * as IBooks from '../../../../interfaces/merchants/resources/books';

import css from './InputBookingDateTimeCalendar.module.scss';

import { getCalendarDate } from './helper';

interface Props {
  postable: boolean;
  baseDate: Date;
  onSubmit: (availability: IBooks.TimeSlotAvailability) => void;
  onChangeBaseDate: (baseDate: Date) => void;
  onChangeSelectedDate: (selectedDate: Date) => void;
  selectedDate?: string;
  availableDates: IBooks.AvailableDates;
  availableTimes: IBooks.AvailableTimes;
}

interface DateType {
  date: number;
  iso: string;
  type: string;
}

interface State {
  calendar: {
    baseDate: Date;
    dates: DateType[];
  };
}

const isAvailable = (date: string, dates: string[]) => {
  let exists = false;
  for (const d of dates) {
    if (d === date) {
      exists = true;
      break;
    }
  }
  return exists;
};

class InputBookingDateTimeCalendar extends React.Component<
  Props & InjectedIntlProps,
  State
> {
  constructor(props: Props & InjectedIntlProps) {
    super(props);
    const { baseDate } = this.props;
    this.state = {
      calendar: { baseDate, dates: [] },
    };
    this.handleClickNext = this.handleClickNext.bind(this);
    this.handleClickPrev = this.handleClickPrev.bind(this);

    this.handleClickDate = this.handleClickDate.bind(this);
    this.handleClickTime = this.handleClickTime.bind(this);
  }

  public async componentDidMount() {
    this.fillCalendarPropsBy(this.state.calendar.baseDate);
  }

  private handleClickNext() {
    const { baseDate } = this.state.calendar;
    this.fillCalendarPropsBy(df.addMonths(baseDate, 1));
  }

  private handleClickPrev() {
    const { baseDate } = this.state.calendar;
    this.fillCalendarPropsBy(df.subMonths(baseDate, 1));
  }

  private async fillCalendarPropsBy(baseDate: Date) {
    const { selectedDate } = this.props;
    const dates = getCalendarDate(baseDate);
    this.setState({
      ...this.state,
      calendar: { baseDate, dates },
    });
    this.props.onChangeBaseDate(baseDate);
    if (selectedDate) {
      this.props.onChangeSelectedDate(new Date(selectedDate));
    }
  }

  private handleClickDate(e: React.MouseEvent<HTMLButtonElement>) {
    const { value } = e.currentTarget;
    if (this.props.selectedDate === value) {
      return;
    }
    this.props.onChangeSelectedDate(new Date(value));
  }

  private handleClickTime(availability: IBooks.EventAvailability) {
    const { selectedDate } = this.props;
    this.props.onSubmit({ date: selectedDate!, ...availability });
  }

  public render() {
    const {
      availableDates,
      availableTimes,
      intl,
      selectedDate,
      postable,
    } = this.props;
    const { calendar } = this.state;
    const candidates = selectedDate ? availableTimes.availabilities : [];

    return (
      <div className={css.InputBookingDateTimeCalendar}>
        <BooksDateCalendarHeader
          baseDate={intl.formatDate(df.format(calendar.baseDate), {
            year: 'numeric',
            month: 'long',
            day: undefined,
          })}
          onClickPrev={this.handleClickPrev}
          onClickNext={this.handleClickNext}
          prevButtonDisabled={df.isThisMonth(calendar.baseDate)}
        />
        <BooksDateCalendarMonth>
          {calendar.dates.map((date, index) => (
            <BooksDateCalendarDay
              key={index}
              value={date.iso}
              date={date.date}
              onClick={this.handleClickDate}
              disabled={
                !(availableDates && isAvailable(date.iso, availableDates.dates))
              }
              current={date.type === 'current'}
              selected={selectedDate === date.iso}
            />
          ))}
        </BooksDateCalendarMonth>
        {candidates.length > 0 ? (
          <BooksLabeledContainer
            label={intl.formatMessage({
              id: 'books.formsLabel.booksTime',
            })}
          >
            {candidates.map((availability: IBooks.EventAvailability) => (
              <BooksSelectTimeAvailability
                key={availability.time}
                value={availability.value}
                onClick={() => this.handleClickTime(availability)}
                reservable={
                  availability.state === IBooks.AvailabilityState.Available
                }
                postable={postable}
              />
            ))}
          </BooksLabeledContainer>
        ) : (
          <BooksLabeledContainer
            label={intl.formatMessage({
              id: 'books.formsLabel.booksTime',
            })}
            contentsPadding={true}
          >
            <span className={css.InputBookingDateTimeCalendar__PlaceHolder}>
              {intl.formatMessage({
                id: 'books.forms.pleaseSelectDate',
              })}
            </span>
          </BooksLabeledContainer>
        )}
      </div>
    );
  }
}

export default injectIntl(InputBookingDateTimeCalendar);
